import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService, URL_LOGIN, URL_REFRESCO } from '../auth/auth.service';

/**
 * Prefija la URL del backend y añade el Bearer token. En 401 intenta una vez
 * refrescar el token y repetir la petición; si el refresco falla, la sesión ha
 * caducado de verdad.
 */
export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);

  const url = req.url.startsWith('http') ? req.url : `${environment.apiUrl}${req.url}`;
  // Un admin puede trabajar sobre otra empresa: la elegida se guarda en el navegador.
  const empresa = localStorage.getItem('bebidas.company');
  const preparar = (original: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> => {
    const headers: Record<string, string> = {};
    // El refresco trae su propio Authorization (el refresh_token): no se pisa.
    if (token && !original.headers.has('Authorization')) headers['Authorization'] = `Bearer ${token}`;
    if (empresa && auth.usuario()?.role === 'admin') headers['X-Company-Id'] = empresa;
    return original.clone({ url, setHeaders: headers });
  };
  const tokenUsado = auth.token();
  const esAutenticacion = req.url.includes(URL_LOGIN) || req.url.includes(URL_REFRESCO);

  return next(preparar(req, tokenUsado)).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || esAutenticacion || !tokenUsado) return throwError(() => error);
      // Otra petición ya refrescó mientras esta volaba: basta con repetirla.
      const refresco = auth.token() && auth.token() !== tokenUsado
        ? Promise.resolve(auth.token())
        : auth.refrescar();
      return from(refresco).pipe(
        switchMap((nuevo) => {
          if (!nuevo) {
            auth.sesionCaducada();
            return throwError(() => error);
          }
          return next(preparar(req, nuevo)).pipe(
            catchError((otra: HttpErrorResponse) => {
              if (otra.status === 401) auth.sesionCaducada();
              return throwError(() => otra);
            }),
          );
        }),
      );
    }),
  );
};
